<template>
  <div data-test="graphics-timerSettings">
    <SidebarSettingsGroup
      v-if="!editingMultiple"
      title="Timer Format"
      collapsible
      :element-type="element.type"
      data-test="graphics-timerSettings-settings"
    >
      <!-- TIMER TYPE -->
      <label class="sidebar-form-label">Type</label>

      <div class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <select
            v-model="timerType"
            data-entity="graphics-timerSettings-timerType"
          >
            <option>Countdown</option>
            <option>Count Up</option>
            <option>Stopwatch</option>
          </select>
        </div>
      </div>

      <div
        v-if="timerType === 'Stopwatch'"
        class="settings-row settings-row-xs"
        data-test="graphics-timerSettings-stopwatch"
      >
        <div class="settings-col-12 settings-col-xs">
          <label class="checkbox"
            >Show hours
            <input
              v-model="showHours"
              type="checkbox"
              data-entity="graphics-timerSettings-showHours"
            />
            <div class="checkmark">
              <span class="checkmark-tick fa-solid fa-check"></span>
            </div>
          </label>
          <label class="checkbox"
            >Show milliseconds
            <input
              v-model="showMilliseconds"
              type="checkbox"
              data-entity="graphics-timerSettings-showMilliseconds"
            />
            <div class="checkmark">
              <span class="checkmark-tick fa-solid fa-check"></span>
            </div>
          </label>
        </div>
      </div>

      <div
        v-if="timerType !== 'Stopwatch'"
        class="settings-row settings-row-xs"
        data-test="graphics-timerSettings-counterFormat"
      >
        <div class="settings-col-12 settings-col-xs">
          <div class="btn-group">
            <button
              :class="[{ selected: counterFormat == 'Time' }]"
              class="btn btn-sm"
              @click="setCounterFormat('Time')"
              data-entity="graphics-timerSettings-counterFormat-Time"
            >
              Time
            </button>
            <button
              :class="[{ selected: counterFormat == 'Date & Time' }]"
              class="btn btn-sm"
              @click="setCounterFormat('Date & Time')"
              data-entity="graphics-timerSettings-counterFormat-DateTime"
            >
              Date & Time
            </button>
          </div>
        </div>
      </div>

      <!-- DISPLAY FORMAT -->
      <div
        v-if="timerType !== 'Stopwatch'"
        data-test="graphics-timerSettings-displayFormat"
      >
        <label class="sidebar-form-label">Display format</label>

        <div class="settings-row settings-row-xs">
          <div class="settings-col-12 settings-col-xs">
            <label class="checkbox"
              >Days
              <input
                v-model="showDays"
                type="checkbox"
                data-entity="graphics-timerSettings-showDays"
              />
              <div class="checkmark">
                <span class="checkmark-tick fas fa-check"></span>
              </div>
            </label>
            <label class="checkbox"
              >Hours
              <input
                v-model="showHours"
                type="checkbox"
                data-entity="graphics-timerSettings-showHours"
              />
              <div class="checkmark">
                <span class="checkmark-tick fas fa-check"></span>
              </div>
            </label>
            <label class="checkbox"
              >Minutes
              <input
                v-model="showMinutes"
                type="checkbox"
                data-entity="graphics-timerSettings-showMinutes"
              />
              <div class="checkmark">
                <span class="checkmark-tick fas fa-check"></span>
              </div>
            </label>
            <label class="checkbox"
              >Seconds
              <input
                v-model="showSeconds"
                type="checkbox"
                data-entity="graphics-timerSettings-showSeconds"
              />
              <div class="checkmark">
                <span class="checkmark-tick fas fa-check"></span>
              </div>
            </label>
          </div>
        </div>
      </div>

      <!-- PREVIEW -->
      <label class="sidebar-form-label">Preview</label>

      <div
        v-if="counterFormat == 'Time'"
        class="timer-labels"
        data-test="graphics-timerSettings-timePreview"
      >
        <div class="timer-label">
          <select
            v-model.number="hourPreview"
            data-entity="graphics-timerSettings-hourPreview"
          >
            <option v-for="hour in hoursList" :key="hour">{{ hour }}</option>
          </select>
          <label class="sidebar-input-sub-label">H</label>
        </div>
        <div class="timer-label">
          <select
            v-model.number="minutePreview"
            data-entity="graphics-timerSettings-minutePreview"
          >
            <option v-for="minute in minutesList" :key="minute">{{ minute }}</option>
          </select>
          <label class="sidebar-input-sub-label">M</label>
        </div>

        <div class="timer-label">
          <select
            v-model.number="secondPreview"
            data-entity="graphics-timerSettings-secondPreview"
          >
            <option v-for="second in secondsList" :key="second">{{ second }}</option>
          </select>
          <label class="sidebar-input-sub-label">S</label>
        </div>
      </div>

      <div
        v-if="counterFormat == 'Date & Time'"
        class="datepicker"
        data-test="graphics-timerSettings-dateTimePicker"
      >
        <!-- Update endDateTime computed in Timer component if the format changes -->
        <date-picker
          v-model:value="dateTime"
          type="datetime"
          time-title-format="MM/DD/YYYY hh:mm A"
          format="MM/DD/YYYY hh:mm A"
          value-type="format"
          use12h
          :clearable="false"
          :disabled-date="disabledDate"
          :disabled-time="disabledTime"
          placeholder="Select date and time"
        >
        </date-picker>
      </div>

      <div class="timer-btn-group">
        <button
          class="timer-btn"
          data-entity="graphics-timerSettings-play"
          @click="handlePlay"
        >
          {{ playBtn }}
        </button>
        <button
          v-if="counterFormat != 'Date & Time'"
          class="timer-btn"
          data-entity="graphics-timerSettings-reset"
          @click="handleReset"
        >
          Reset
        </button>
      </div>
    </SidebarSettingsGroup>

    <SidebarSettingsGroup
      title="Style & Animation"
      collapsible
      :element-type="element.type"
      data-test="graphics-timerSettings-styleAndAnimation"
    >
      <!-- STYLE & ANIMATION -->
      <template v-if="!editingMultiple">
        <!-- OPACITY -->
        <opacity-settings v-model="timerTextOpacity" />

        <!-- STYLE -->
        <label class="sidebar-form-label">Style</label>

        <gap-settings
          v-model="gap"
          action="timerSettings-gap"
          help-text="Space between items"
        />

        <div class="settings-row settings-row-xs">
          <div class="settings-col-12 settings-col-xs">
            <label
              class="checkbox"
              :class="{ 'checkbox-disabled': showUnits }"
            >Show dividers
              <input
                v-model="showDividers"
                type="checkbox"
                :disabled="showUnits"
                data-entity="graphics-timerSettings-showDividers"
              />
              <div class="checkmark">
                <span class="checkmark-tick fas fa-check"></span>
              </div>
            </label>
          </div>
        </div>

        <div
          v-if="timerType !== 'Stopwatch'"
          data-test="graphics-timerSettings-clockStyle"
        >
          <div class="settings-row settings-row-xs">
            <div class="settings-col-12 settings-col-xs">
              <select v-model="style">
                <option>Basic</option>
                <option>Flip clock</option>
              </select>
            </div>
          </div>
        </div>

        <!-- FLIP CLOCK COLOR PICKER -->
        <div
          v-if="style == 'Flip clock'"
          class="settings-row settings-row-xs"
          data-test="graphics-timerSettings-flipClockColor"
        >
          <div class="settings-col-12 settings-col-xs">
            <div class="colorpicker">
              <colorpicker v-model="flipClockColor" />
            </div>
            <label class="sidebar-input-sub-label">"Flaps" color</label>
          </div>
        </div>

        <!-- FONT -->
        <label class="sidebar-form-label">Font</label>

        <font-dropdown
          :custom-fonts="customFontsArray"
          :element="element"
          text-type="timerText"
        />

        <div class="settings-row settings-row-xs">
          <div class="settings-col-4 settings-col-xs">
            <input
              v-model="timerTextFontSize"
              type="number"
              name="font"
              min="10"
              max="100"
              data-entity="graphics-timerSettings-timerTextFontSize"
            />
            <label class="sidebar-input-sub-label">Size</label>
          </div>
          <div class="settings-col-4 settings-col-xs">
            <validation-field as="input"
              v-model.number="timerTextLetterSpacing"
              rules="required|numeric|min:0"
              type="number"
              name="timerTextLetterSpacing"
              min="0"
              :class="{ 'is-invalid': validationErrors['timerTextLetterSpacing'] }"
              data-entity="graphics-timerSettings-timerTextLetterSpacing"
            />
            <label class="sidebar-input-sub-label">Letter spacing</label>
          </div>
          <div class="settings-col-4 settings-col-xs">
            <validation-field as="input"
              v-model.number="timerTextLineHeight"
              rules="required|decimal|min:0"
              type="number"
              name="timerTextLineHeight"
              min="0"
              step="0.1"
              :disabled="style === 'Flip clock'"
              :class="{ 'is-invalid': validationErrors['timerTextLineHeight'] }"
              data-entity="graphics-timerSettings-timerTextLineHeight"
            />
            <label class="sidebar-input-sub-label">Line Spacing</label>
          </div>

          <!-- COLOR PICKER -->
          <div class="settings-col-6 settings-col-xs">
            <div class="colorpicker">
              <colorpicker v-model="timerTextColor" />
            </div>
            <label class="sidebar-input-sub-label">Color</label>
          </div>
        </div>

        <!-- TEXT STYLES -->
        <label class="sidebar-form-label">Text styles</label>

        <div class="settings-row settings-row-xs text-styles">
          <div class="settings-col-12 settings-col-xs simple">
            <button
              class="btn btn-sm btn-icon"
              :class="getFontWeightBtnClass('timerText')"
              data-entity="graphics-timerSettings-timerTextBold"
              @click="setFontWeight('timerText')"
            >
              <span class="fa-solid fa-bold"></span>
            </button>
            <button
              class="btn btn-sm btn-icon"
              :class="getFontStyleBtnClass('timerText')"
              data-entity="graphics-timerSettings-timerTextItalic"
              @click="setFontStyle('timerText')"
            >
              <span class="fa-solid fa-italic"></span>
            </button>
          </div>
        </div>
      </template>

      <!-- TEXT SHADOW -->
      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox"
          >Text shadow
          <input
            v-model="textShadow"
            type="checkbox"
            data-entity="graphics-timerSettings-textShadow"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check"></span>
          </div>
        </label>

        <div v-if="textShadow" class="settings-row settings-row-xs">
          <div class="settings-col-3 settings-col-xs">
            <validation-field as="input"
              v-model.number="textShadowX"
               :rules="`${editingMultiple ? '' : 'required|'}numeric`"
              type="number"
              name="textShadowX"
              :class="{ 'is-invalid': validationErrors['textShadowX'] }"
              data-entity="graphics-timerSettings-textShadowX"
            />
            <label class="sidebar-input-sub-label">X</label>
          </div>
          <div class="settings-col-3 settings-col-xs">
            <validation-field as="input"
              v-model.number="textShadowY"
               :rules="`${editingMultiple ? '' : 'required|'}numeric`"
              type="number"
              name="textShadowY"
              :class="{ 'is-invalid': validationErrors['textShadowY'] }"
              data-entity="graphics-timerSettings-textShadowY"
            />
            <label class="sidebar-input-sub-label">Y</label>
          </div>
          <div class="settings-col-3 settings-col-xs">
            <validation-field as="input"
              v-model.number="textShadowBlur"
               :rules="`${editingMultiple ? '' : 'required|'}numeric`"
              type="number"
              name="textShadowBlur"
              :class="{ 'is-invalid': validationErrors['textShadowBlur'] }"
              data-entity="graphics-timerSettings-textShadowBlur"
            />
            <label class="sidebar-input-sub-label">Blur</label>
          </div>
          <div class="settings-col-3 settings-col-xs colorpicker">
            <colorpicker v-model="textShadowColor" />
            <label class="sidebar-input-sub-label">Color</label>
          </div>
        </div>
      </div>

      <!-- SHOW END DATE & TIME -->
      <div
        v-if="!editingMultiple && counterFormat == 'Date & Time'"
        data-test="graphics-timerSettings-dateTimeSettings"
      >
        <hr class="sidebar-divider" />

        <div class="settings-row settings-row-xs">
          <div class="settings-col-12 settings-col-xs">
            <label class="checkbox"
              >Show end date & time
              <input
                v-model="showEndDateTime"
                type="checkbox"
                data-entity="graphics-timerSettings-showEndDateTime"
              />
              <div class="checkmark">
                <span class="checkmark-tick fa-solid fa-check"></span>
              </div>
            </label>
          </div>
        </div>

        <div
          v-if="showEndDateTime"
          data-test="graphics-timerSettings-endDateTimeSettings"
        >
          <div class="settings-row settings-row-xs">
            <div class="settings-col-12 settings-col-xs">
              <label class="sidebar-form-label">Format</label>
              <select
                v-model="endDateTimeFormat"
                data-entity="graphics-timerSettings-endDateTimeFormat"
              >
                <option>DD/MM/YYYY hh:mm a</option>
                <option>MM/DD/YYYY hh:mm a</option>
                <option>DD/MM/YYYY HH:mm a</option>
                <option>MM/DD/YYYY HH:mm a</option>
              </select>
            </div>
          </div>

          <!-- OPACITY -->
          <opacity-settings v-model="endDateTextOpacity" />

          <!-- FONT -->
          <label class="sidebar-form-label">Font</label>

          <font-dropdown
            :custom-fonts="customFontsArray"
            :element="element"
            text-type="endDateText"
          />

          <div class="settings-row settings-row-xs">
            <div class="settings-col-4 settings-col-xs">
              <input
                v-model="endDateTextFontSize"
                type="number"
                name="font"
                min="10"
                max="100"
                data-entity="graphics-timerSettings-endDateTextFontSize"
              />
              <label class="sidebar-input-sub-label">Size</label>
            </div>
            <!-- LETTER SPACING -->
            <div class="settings-col-4 settings-col-xs">
              <input
                v-model="endDateTextLetterSpacing"
                type="number"
                name="font"
                min="0"
                max="10"
                data-entity="graphics-timerSettings-endDateTextLetterSpacing"
              />
              <label class="sidebar-input-sub-label">Letter spacing</label>
            </div>
            <div class="settings-col-4 settings-col-xs">
              <validation-field as="input"
                v-model.number="endDateTextLineHeight"
                rules="required|decimal|min:0"
                type="number"
                name="endDateTextLineHeight"
                min="0"
                step="0.1"
                :class="{ 'is-invalid': validationErrors['endDateTextLineHeight'] }"
                data-entity="graphics-timerSettings-endDateTextLineHeight"
              />
              <label class="sidebar-input-sub-label">Line Spacing</label>
            </div>
          </div>

          <!-- COLOR PICKER -->
          <div class="settings-row settings-row-xs">
            <div class="settings-col-12 settings-col-xs">
              <div class="colorpicker">
                <colorpicker v-model="endDateTextColor" />
              </div>
              <label class="sidebar-input-sub-label">Color</label>
            </div>
          </div>

          <!-- TEXT STYLES -->
          <label class="sidebar-form-label">Text styles</label>

          <div class="settings-row settings-row-xs text-styles">
            <div class="settings-col-12 settings-col-xs simple">
              <button
                class="btn btn-sm btn-icon"
                :class="getFontWeightBtnClass('endDateText')"
                data-entity="graphics-timerSettings-endDateTextBold"
                @click="setFontWeight('endDateText')"
              >
                <span class="fa-solid fa-bold"></span>
              </button>
              <button
                class="btn btn-sm btn-icon"
                :class="getFontStyleBtnClass('endDateText')"
                data-entity="graphics-timerSettings-endDateTextItalic"
                @click="setFontStyle('endDateText')"
              >
                <span class="fa-solid fa-italic"></span>
              </button>
            </div>
          </div>

          <!-- TEXT OPTIONS -->
          <label class="sidebar-form-label">Text options</label>

          <div class="settings-row settings-row-xs">
            <div class="settings-col-12 settings-col-xs">
              <div class="btn-group text-transforms">
                <button
                  type="button"
                  v-for="style in textTransforms"
                  :key="style.value"
                  :class="{
                    'btn btn-sm': true,
                    selected:
                      element.styles.endDateText.textTransform ===
                      style.value,
                  }"
                  :data-entity="`graphics-timerSettings-endDateTextTransform${style.value}`"
                  @click="setTransformStyle('endDateText', style.value)"
                >
                  <span class="icon" :class="'icon-' + style.icon"></span>
                </button>
              </div>
              <label class="sidebar-input-sub-label">Transform</label>
            </div>
          </div>
        </div>
      </div>

      <hr v-if="!editingMultiple" class="sidebar-divider" />

      <!-- SHOW UNITS -->
      <div
        v-if="!editingMultiple && timerType !== 'Stopwatch'"
        data-test="graphics-timerSettings-showUnits"
      >
        <div class="settings-row settings-row-xs">
          <div class="settings-col-12 settings-col-xs">
            <label class="checkbox header-checkbox">Show units
              <input
                v-model="showUnits"
                type="checkbox"
                data-entity="graphics-timerSettings-showUnits"
              />
              <div class="checkmark">
                <span class="checkmark-tick fa-solid fa-check"></span>
              </div>
            </label>
          </div>
        </div>

        <div
          v-if="showUnits"
          data-test="graphics-timerSettings-unitSettings"
        >
          <label class="sidebar-form-label">Labels</label>

          <div class="timer-labels">
            <div v-if="showDays" class="timer-label">
              <input
                v-model="daysLabel"
                data-entity="graphics-timerSettings-daysLabel"
              />
              <label class="sidebar-input-sub-label">D</label>
            </div>

            <div v-if="showHours" class="timer-label">
              <input
                v-model="hoursLabel"
                data-entity="graphics-timerSettings-hoursLabel"
              />
              <label class="sidebar-input-sub-label">H</label>
            </div>

            <div v-if="showDays" class="break"></div>

            <div v-if="showMinutes" class="timer-label">
              <input
                v-model="minutesLabel"
                data-entity="graphics-timerSettings-minutesLabel"
              />
              <label class="sidebar-input-sub-label">M</label>
            </div>

            <div
              v-if="!showDays && showHours"
              class="break"></div>

            <div v-if="showSeconds" class="timer-label">
              <input
                v-model="secondsLabel"
                data-entity="graphics-timerSettings-secondsLabel"
              />
              <label class="sidebar-input-sub-label">S</label>
            </div>
          </div>

          <!-- OPACITY -->
          <opacity-settings v-model="unitTextOpacity" />

          <!-- FONT -->
          <label class="sidebar-form-label">Font</label>

          <font-dropdown
            :custom-fonts="customFontsArray"
            :element="element"
            text-type="unitText"
          />

          <div class="settings-row settings-row-xs">
            <div class="settings-col-4 settings-col-xs">
              <input
                v-model="unitTextFontSize"
                type="number"
                name="font"
                min="10"
                max="100"
                data-entity="graphics-timerSettings-unitTextFontSize"
              />
              <label class="sidebar-input-sub-label">Size</label>
            </div>
            <!-- LETTER SPACING -->
            <div class="settings-col-4 settings-col-xs">
              <input
                v-model="unitTextLetterSpacing"
                type="number"
                name="font"
                min="0"
                max="10"
                data-entity="graphics-timerSettings-unitTextLetterSpacing"
              />
              <label class="sidebar-input-sub-label">Letter spacing</label>
            </div>
            <div class="settings-col-4 settings-col-xs">
              <validation-field as="input"
                v-model.number="unitTextLineHeight"
                rules="required|decimal|min:0"
                type="number"
                name="unitTextLineHeight"
                min="0"
                step="0.1"
                :class="{ 'is-invalid': validationErrors['unitTextLineHeight'] }"
                data-entity="graphics-timerSettings-unitTextLineHeight"
              />
              <label class="sidebar-input-sub-label">Line Spacing</label>
            </div>
          </div>

          <!-- COLOR PICKER -->
          <div class="settings-row settings-row-xs">
            <div class="settings-col-12 settings-col-xs">
              <div class="colorpicker">
                <colorpicker v-model="unitTextColor" />
              </div>
              <label class="sidebar-input-sub-label">Color</label>
            </div>
          </div>

          <!-- TEXT STYLES -->
          <label class="sidebar-form-label">Text styles</label>

          <div class="settings-row settings-row-xs text-styles">
            <div class="settings-col-12 settings-col-xs simple">
              <button
                class="btn btn-sm btn-icon"
                :class="getFontWeightBtnClass('unitText')"
                data-entity="graphics-timerSettings-unitTextBold"
                @click="setFontWeight('unitText')"
              >
                <span class="fa-solid fa-bold"></span>
              </button>
              <button
                class="btn btn-sm btn-icon"
                :class="getFontStyleBtnClass('unitText')"
                data-entity="graphics-timerSettings-unitTextItalic"
                @click="setFontStyle('unitText')"
              >
                <span class="fa-solid fa-italic"></span>
              </button>
            </div>
          </div>

          <!-- TEXT OPTIONS -->
          <label class="sidebar-form-label">Text options</label>

          <div class="settings-row settings-row-xs">
            <div class="settings-col-12 settings-col-xs">
              <div class="btn-group text-transforms">
                <button
                  type="button"
                  v-for="style in textTransforms"
                  :key="style.value"
                  :class="{
                    'btn btn-sm': true,
                    selected:
                      element.styles.unitText.textTransform === style.value,
                  }"
                  :data-entity="`graphics-timerSettings-unitTextTransform${style.value}`"
                  @click="setTransformStyle('unitText', style.value)"
                >
                  <span class="icon" :class="'icon-' + style.icon"></span>
                </button>
              </div>
              <label class="sidebar-input-sub-label">Transform</label>
            </div>
          </div>
        </div>
      </div>

      <hr class="sidebar-divider" />
      <animation-settings :element="element"></animation-settings>
    </SidebarSettingsGroup>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import datePicker from 'vue-datepicker-next';
import eventBus from '../../utils/event-bus';
import SettingsMixins from '../../mixins/settings-mixins';

import animationSettings from './Animation.vue';
import OpacitySettings from './Opacity.vue';
import FontDropdown from './FontDropdown.vue';
import GapSettings from './Gap.vue';

import dayjs from 'dayjs';

const arr24 = [...Array(24).keys()];
const arr60 = [...Array(60).keys()];

export default {
  name: 'TimerSettings',
  components: {
    datePicker,
    animationSettings,
    OpacitySettings,
    FontDropdown,
    GapSettings,
  },
  mixins: [SettingsMixins],
  props: ['element'],
  data() {
    return {
      hoursList: [...arr24],
      minutesList: [...arr60],
      secondsList: [...arr60],
      textTransforms: [
        { value: 'capitalize', icon: 'text-normal' },
        { value: 'uppercase', icon: 'text-uppercase' },
        { value: 'lowercase', icon: 'text-lowercase' },
      ],
    };
  },
  computed: {
    ...mapGetters(['customFontsArray', 'customFonts']),
    timerType: {
      get() {
        return this.element.timerType;
      },
      set(v) {
        this.updateOneElementValue('timerType', v);
      }
    },
    counterFormat: {
      get() {
        return this.element.counterFormat;
      },
      set(v) {
        this.updateOneElementValue('counterFormat', v);
      }
    },
    showDays: {
      get() {
        return this.element.showDays;
      },
      set(v) {
        this.updateOneElementValue('showDays', v);
      }
    },
    daysLabel: {
      get() {
        return this.element.daysLabel;
      },
      set(v) {
        this.updateOneElementValue('daysLabel', v);
      }
    },
    showHours: {
      get() {
        return this.element.showHours;
      },
      set(v) {
        this.updateOneElementValue('showHours', v);
      }
    },
    hoursLabel: {
      get() {
        return this.element.hoursLabel;
      },
      set(v) {
        this.updateOneElementValue('hoursLabel', v);
      }
    },
    showMinutes: {
      get() {
        return this.element.showMinutes;
      },
      set(v) {
        this.updateOneElementValue('showMinutes', v);
      }
    },
    minutesLabel: {
      get() {
        return this.element.minutesLabel;
      },
      set(v) {
        this.updateOneElementValue('minutesLabel', v);
      }
    },
    showSeconds: {
      get() {
        return this.element.showSeconds;
      },
      set(v) {
        this.updateOneElementValue('showSeconds', v);
      }
    },
    secondsLabel: {
      get() {
        return this.element.secondsLabel;
      },
      set(v) {
        this.updateOneElementValue('secondsLabel', v);
      }
    },
    showMilliseconds: {
      get() {
        return this.element.showMilliseconds;
      },
      set(v) {
        this.updateOneElementValue('showMilliseconds', v);
      }
    },
    timerFormat: {
      get() {
        return this.element.timerFormat;
      },
      set(v) {
        this.updateOneElementValue('timerFormat', v);
      }
    },
    hourPreview: {
      get() {
        return this.element.hourPreview;
      },
      set(v) {
        this.updateOneElementValue('hourPreview', v);
      }
    },
    minutePreview: {
      get() {
        return this.element.minutePreview;
      },
      set(v) {
        this.updateOneElementValue('minutePreview', v);
      }
    },
    secondPreview: {
      get() {
        return this.element.secondPreview;
      },
      set(v) {
        this.updateOneElementValue('secondPreview', v);
      }
    },
    dateTime: {
      get() {
        return this.element.dateTime;
      },
      set(v) {
        this.updateOneElementValue('dateTime', v);
      }
    },
    style: {
      get() {
        return this.element.style;
      },
      set(v) {
        this.updateOneElementValue('style', v);
      }
    },
    gap: {
      get() {
        return this.element.gap ?? 0;
      },
      set(v) {
        this.updateOneElementValue('gap', v);
      }
    },
    textShadow: {
      get() {
        return this.getPropertyValue('textShadow');
      },
      set(v) {
        this.updateOneElementValue('textShadow', v);
      }
    },
    textShadowX: {
      get() {
        return this.getPropertyValue('textShadowX');
      },
      set(v) {
        this.updateOneElementValue('textShadowX', v);
      }
    },
    textShadowY: {
      get() {
        return this.getPropertyValue('textShadowY');
      },
      set(v) {
        this.updateOneElementValue('textShadowY', v);
      }
    },
    textShadowBlur: {
      get() {
        return this.getPropertyValue('textShadowBlur');
      },
      set(v) {
        this.updateOneElementValue('textShadowBlur', v);
      }
    },
    textShadowColor: {
      get() {
        return this.getPropertyValue('textShadowColor');
      },
      set(v) {
        this.updateOneElementValue('textShadowColor', v);
      }
    },
    flipClockColor: {
      get() {
        return this.element.flipClockColor;
      },
      set(v) {
        this.updateOneElementValue('flipClockColor', v);
      }
    },
    showUnits: {
      get() {
        return this.element.showUnits;
      },
      set(v) {
        this.updateOneElementValue('showUnits', v);
      }
    },
    showDividers: {
      get() {
        // default to true for timers saved before this setting existed
        return this.getPropertyValue('showDividers', { fallbackValue: true });
      },
      set(v) {
        this.updateOneElementValue('showDividers', v);
      }
    },
    showEndDateTime: {
      get() {
        return this.element.showEndDateTime;
      },
      set(v) {
        this.updateOneElementValue('showEndDateTime', v);
      }
    },
    endDateTimeFormat: {
      get() {
        return this.element.endDateTimeFormat;
      },
      set(v) {
        this.updateOneElementValue('endDateTimeFormat', v);
      }
    },
    timerTextFontSize: {
      get() {
        return this.element.styles.timerText.fontSize;
      },
      set(v) {
        this.updateOneElementValue('styles.timerText.fontSize', v);
      }
    },
    timerTextLetterSpacing: {
      get() {
        return this.getPropertyValue('styles.timerText.letterSpacing', { fallbackValue: 0 });
      },
      set(v) {
        this.updateOneElementValue('styles.timerText.letterSpacing', v);
      },
    },
    timerTextLineHeight: {
      get() {
        return this.getPropertyValue('styles.timerText.lineHeight', { fallbackValue: 1 });
      },
      set(v) {
        this.updateOneElementValue('styles.timerText.lineHeight', v);
      },
    },
    timerTextColor: {
      get() {
        return this.element.styles.timerText.color;
      },
      set(v) {
        this.updateOneElementValue('styles.timerText.color', v);
      }
    },
    timerTextOpacity: {
      get() {
        return this.element.styles.timerText.opacity;
      },
      set(v) {
        this.updateOneElementValue('styles.timerText.opacity', v);
      }
    },
    endDateTextOpacity: {
      get() {
        return this.element.styles.endDateText.opacity;
      },
      set(v) {
        this.updateOneElementValue('styles.endDateText.opacity', v);
      }
    },
    endDateTextFontSize: {
      get() {
        return this.element.styles.endDateText.fontSize;
      },
      set(v) {
        this.updateOneElementValue('styles.endDateText.fontSize', v);
      }
    },
    endDateTextLetterSpacing: {
      get() {
        return this.element.styles.endDateText.letterSpacing;
      },
      set(v) {
        this.updateOneElementValue('styles.endDateText.letterSpacing', v);
      }
    },
    endDateTextLineHeight: {
      get() {
        return this.getPropertyValue('styles.endDateText.lineHeight', { fallbackValue: 1 });
      },
      set(v) {
        this.updateOneElementValue('styles.endDateText.lineHeight', v);
      }
    },
    endDateTextColor: {
      get() {
        return this.element.styles.endDateText.color;
      },
      set(v) {
        this.updateOneElementValue('styles.endDateText.color', v);
      }
    },
    unitTextOpacity: {
      get() {
        return this.element.styles.unitText.opacity;
      },
      set(v) {
        this.updateOneElementValue('styles.unitText.opacity', v);
      }
    },
    unitTextFontSize: {
      get() {
        return this.element.styles.unitText.fontSize;
      },
      set(v) {
        this.updateOneElementValue('styles.unitText.fontSize', v);
      }
    },
    unitTextLetterSpacing: {
      get() {
        return this.element.styles.unitText.letterSpacing;
      },
      set(v) {
        this.updateOneElementValue('styles.unitText.letterSpacing', v);
      }
    },
    unitTextLineHeight: {
      get() {
        return this.getPropertyValue('styles.unitText.lineHeight', { fallbackValue: 1 });
      },
      set(v) {
        this.updateOneElementValue('styles.unitText.lineHeight', v);
      },
    },
    unitTextColor: {
      get() {
        return this.element.styles.unitText.color;
      },
      set(v) {
        this.updateOneElementValue('styles.unitText.color', v);
      }
    },

    playBtn() {
      const text = this.element.play
        ? this.timerType == 'Stopwatch'
          ? 'Stop'
          : 'Pause'
        : this.timerType == 'Stopwatch'
        ? 'Start'
        : 'Play';

      return text;
    },

    getFontName() {
      return this.fonts.filter(
        (font) => font.font === this.element.styles.fontFamily,
      )[0];
    },
  },
  watch: {
    timerType(newVal) {
      // reset default countdown/countup timer format
      if (newVal !== 'Stopwatch') {
        this.counterFormat = 'Time';
        this.showMilliseconds = false;
      } else {
        this.showUnits = false;
      }

      // turn off flip clock for stopwatch
      if (newVal === 'Stopwatch') this.style = 'Basic';

      this.resetTimerFormat();
      this.resetPreview();
    },
  },

  methods: {
    resetPreview() {
      this.hourPreview = 0;
      this.minutePreview = 0;
      this.secondPreview = 0;
    },
    setCounterFormat(type) {
      this.counterFormat = type;

      this.resetTimerFormat();
      this.resetPreview();
    },
    resetTimerFormat() {
      // set default units based on countdown type
      this.showDays = this.counterFormat != 'Time';
      this.showHours = true;
      this.showMinutes = true;
      this.showSeconds = true;

      // set default format for stopwatch
      if (this.timerType == 'Stopwatch') {
        this.counterFormat = null;
      }
    },
    selectedFont(textType) {
      return this.fonts.filter(
        (fontType) => fontType.font == this.element.styles[textType].fontFamily
      )[0];
    },
    getFontWeightBtnClass(textType) {
      return this.element.styles[textType].fontWeight === 'bold'
        ? 'btn-style-a'
        : 'btn-style-d';
    },
    getFontStyleBtnClass(textType) {
      return this.element.styles[textType].fontStyle === 'italic'
        ? 'btn-style-a'
        : 'btn-style-d';
    },
    setFont(textType, fontType) {
      this.updateOneElementValue(`styles.${textType}.fontFamily`, fontType.font);
    },
    setFontWeight(textType) {
      const fontWeight =
        this.element.styles[textType].fontWeight == 'normal'
          ? 'bold'
          : 'normal';
      this.updateOneElementValue(`styles.${textType}.fontWeight`, fontWeight);
    },
    setFontStyle(textType) {
      const fontStyle =
        this.element.styles[textType].fontStyle == 'normal'
          ? 'italic'
          : 'normal';
      this.updateOneElementValue(`styles.${textType}.fontStyle`, fontStyle);
    },
    setTransformStyle(textType, style) {
      if (this.element.styles[textType].textTransform == style) {
        this.updateOneElementValue(`styles.${textType}.textTransform`, 'none');
      } else {
        this.updateOneElementValue(`styles.${textType}.textTransform`, style);
      }
    },
    handlePlay(event) {
      // toggle play
      // do not record mutation of play in history
      this.updateOneElementValue('play', !this.element.play, this.element.id, { blacklist: true });
    },
    handleReset(event) {
      // stop play, reset timer
      // do not record mutation of play in history
      this.updateOneElementValue('play', false, this.element.id, { blacklist: true });
      eventBus.emit('reset-timer', this.element.id);
    },
    disabledDate(date) {
      // return true if date should be disabled
      if (this.timerType === 'Countdown') {
        return dayjs(date).isBefore(
          dayjs().hour(0).minute(0).second(0).format()
        );
      }
      return dayjs(date).isAfter(dayjs().hour(0).minute(0).second(0).format());
    },
    disabledTime(date) {
      // return true if date(time) should be disabled
      if (this.timerType == 'Countdown') {
        return dayjs(date).isBefore();
      }
      return dayjs(date).isAfter();
    },
  },
};
</script>
